import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bell, Menu } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { useNotificationsUi, useNotificationsWs } from '@entities/notification'
import { ParticipantBalanceChip } from '@entities/payment/ui/ParticipantBalanceChip'
import { participantProfileQuery } from '@entities/user'
import { apiClient } from '@shared/api/client'
import { endpoints } from '@shared/api/endpoints'
import { QUERY_CACHE_KEYS } from '@shared/api/queryKeys'
import { useAuth } from '@shared/auth/authContext'
import { ROLES, type UserRole } from '@shared/config/constants'
import { ROUTES } from '@shared/config/routes'
import { AppLocale } from '@shared/i18n/locales'
import { resolveImageUrl } from '@shared/lib'
import { Avatar, Button, ButtonVariant, LanguageSelector } from '@shared/ui'
import { NotificationsPopup } from './NotificationsPopup'

interface CurrentProfile {
  full_name_ru: string | null
  full_name_en: string | null
  image: string | null
}

function NotificationBellButton() {
  const { t } = useTranslation('admin')
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const { notifications, setOpen } = useNotificationsUi()
  useNotificationsWs(true)

  return (
    <div ref={setContainer} className="relative">
      <Button
        variant={ButtonVariant.ghost}
        onClick={() => setOpen(true)}
        aria-label={t('notification.nots-popup')}
      >
        <Bell className="h-5 w-5" />
      </Button>
      {notifications.length > 0 && (
        <span className="bg-orange pointer-events-none absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full text-[10px] text-white">
          {notifications.length > 9 ? '9+' : notifications.length}
        </span>
      )}

      <NotificationsPopup container={container} />
    </div>
  )
}

interface Props {
  onMenuToggle: () => void
}

const PROFILE_ROUTE: Record<UserRole, string> = {
  [ROLES.ADMIN]: ROUTES.ADMIN_PROFILE,
  [ROLES.PARTICIPANT]: ROUTES.PARTICIPANT_PERSONAL,
  [ROLES.REPRESENTATIVE]: ROUTES.REPRESENTATIVE_PROFILE,
}

export function Header({ onMenuToggle }: Props) {
  const { user, role } = useAuth()
  const { t, i18n } = useTranslation('common')

  const { data: participantProfile } = useQuery({
    ...participantProfileQuery,
    enabled: role === ROLES.PARTICIPANT,
  })
  const { data: repProfile } = useQuery<CurrentProfile>({
    queryKey: QUERY_CACHE_KEYS.profile.representative,
    queryFn: () =>
      apiClient.get<CurrentProfile>(endpoints.profile.representative).then((r) => r.data),
    enabled: role === ROLES.REPRESENTATIVE,
  })
  const { data: adminProfile } = useQuery<CurrentProfile>({
    queryKey: QUERY_CACHE_KEYS.profile.admin,
    queryFn: () => apiClient.get<CurrentProfile>(endpoints.profile.admin).then((r) => r.data),
    enabled: role === ROLES.ADMIN,
  })

  const liveProfile: CurrentProfile | undefined =
    role === ROLES.PARTICIPANT
      ? participantProfile
      : role === ROLES.REPRESENTATIVE
        ? repProfile
        : role === ROLES.ADMIN
          ? adminProfile
          : undefined

  const fallbackProfile = user?.profile
  const currentLang = i18n.language as AppLocale

  const fullNameRu = liveProfile?.full_name_ru ?? fallbackProfile?.full_name_ru ?? ''
  const fullNameEn = liveProfile?.full_name_en ?? fallbackProfile?.full_name_en ?? ''
  const displayName =
    currentLang === AppLocale.en ? fullNameEn || fullNameRu : fullNameRu || fullNameEn

  const imagePath = liveProfile?.image ?? fallbackProfile?.image ?? null
  const avatarUrl = imagePath ? resolveImageUrl(imagePath) : undefined

  const profileRoute = role ? PROFILE_ROUTE[role] : ROUTES.LOGIN

  const firstName = displayName.split(' ')[0]

  return (
    <header className="border-border bg-surface flex h-16 items-center justify-between gap-4 border-b px-4 lg:px-8">
      {/* Left: mobile toggle + welcome text */}
      <div className="flex min-w-0 items-center gap-3">
        <Button
          type="button"
          onClick={onMenuToggle}
          variant="ghost"
          className="text-secondary h-auto p-0 lg:hidden"
          aria-label="Toggle menu"
        >
          <Menu size={22} />
        </Button>
        {/* Приветствие уступает первым: на 1024 оно и пилюля баланса сходились
            вплотную, потому что обе стороны были `nowrap` и ужиматься было
            нечему. Длинное имя теперь обрезается, а не толкает правую группу. */}
        <p className="hidden truncate text-xl leading-[1.24] font-normal lg:block">
          {t('welcome', { name: firstName })}
        </p>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-4">
        {role === ROLES.PARTICIPANT && <ParticipantBalanceChip />}
        <LanguageSelector />
        {role && <NotificationBellButton />}

        {/* User profile — clickable, navigates to profile page */}
        <NavLink to={profileRoute} className="flex items-center gap-3">
          <Avatar src={avatarUrl} name={displayName} size="md" />
          <div className="hidden min-w-0 flex-col gap-0.5 sm:flex">
            <span className="text-foreground text-base leading-[1.24] font-bold whitespace-nowrap">
              {displayName}
            </span>
            <span className="text-foreground text-base leading-none whitespace-nowrap">
              {role ? t(`role.${role}`) : ''}
            </span>
          </div>
        </NavLink>
      </div>
    </header>
  )
}
